import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Bell, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { SiFacebook, SiInstagram, SiTiktok, SiWhatsapp, SiYoutube } from 'react-icons/si';
import { subscribe } from '@/services/api';

const Footer = () => {
  const [siteIcon, setSiteIcon] = useState<string | null>(null);
  const [socials, setSocials] = useState<{
    facebook?: { handle: string; url: string };
    youtube?: { handle: string; url: string };
    tiktok?: { handle: string; url: string };
    instagram?: { handle: string; url: string };
    twitter?: { handle: string; url: string };
    whatsapp?: { handle: string; url: string };
  }>({});

  // --- Subscription States ---
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchSiteIcon = async () => {
      try {
        const response = await axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/footer_logo');
        setSiteIcon(response.data);
      } catch (error) {
        console.error('Failed to fetch site icon', error);
      }
    };

    const fetchSocialLinks = async () => {
      try {
        const res = await axios.get('https://api.ibadansouthanglicandiocese.org.ng/api/v1/social_handles');
        setSocials(res.data);
      } catch (err) {
        console.error('Failed to fetch social handles', err);
      }
    };

    fetchSiteIcon();
    fetchSocialLinks();
  }, []);

  // --- Form Handler ---
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      await subscribe({ email: email });
      setStatus('success');
      setMessage('Thank you for joining our community!');
      setEmail('');
      // Reset back to idle after 5 seconds
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err: any) {
      setStatus('error');
      setMessage(err.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* --- NEWSLETTER SECTION START --- */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-slate-800/50 rounded-[3rem] p-8 md:p-12 overflow-hidden border border-white/5 mb-16"
        >
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#f59e0b_1px,transparent_1px)] [background-size:30px_30px]" />
          </div>

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-md text-center lg:text-left">
              <div className="inline-flex items-center gap-2 text-amber-500 text-[10px] font-bold uppercase tracking-widest mb-3">
                <Bell size={14} /> Stay Informed
              </div>
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-2">
                Subscribe to the <span className="italic text-amber-500">Diocesan Call</span>
              </h3>
              <p className="text-gray-400 text-sm font-serif italic">
                Receive spiritual reflections and news directly in your inbox.
              </p>
            </div>

            <div className="w-full max-w-md">
              <form onSubmit={handleSubscribe} className="relative">
                <div className="relative flex flex-col sm:flex-row gap-3 p-1.5 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    className="flex-grow bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-gray-500 font-serif disabled:opacity-50"
                    required
                    disabled={status === 'loading'}
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-800 text-slate-900 px-6 py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 transition-all group min-w-[120px]"
                  >
                    {status === 'loading' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <>Join <Send size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" /></>
                    )}
                  </button>
                </div>
              </form>

              {/* Status Message Feedback */}
              <AnimatePresence mode="wait">
                {status === 'success' && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 text-emerald-400 text-xs flex items-center gap-2 font-serif italic"
                  >
                    <CheckCircle2 size={14} /> {message}
                  </motion.p>
                )}
                {status === 'error' && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 text-red-400 text-xs flex items-center gap-2 font-serif italic"
                  >
                    <AlertCircle size={14} /> {message}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
        {/* --- NEWSLETTER SECTION END --- */}

        {/* ... Rest of your footer (Diocese Info, Links, Resources, Contact) ... */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Diocese Info */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <div className="max-h-20 overflow-hidden flex items-start">
                {siteIcon ? (
                  <img src={siteIcon} alt="Site Icon" className="h-16 w-auto object-contain" />
                ) : (
                  <span className="text-white font-bold text-lg bg-amber-600 p-3 rounded-lg">✝</span>
                )}
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              Serving the Anglican faithful in Ibadan South with dedication to spreading the Gospel
              and building God's kingdom on earth.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 border-b border-gray-800 pb-2">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-gray-400 hover:text-amber-500 transition-colors">About Us</Link></li>
              <li><Link to="/bishop" className="text-gray-400 hover:text-amber-500 transition-colors">Our Bishop</Link></li>
              <li><Link to="/clergy" className="text-gray-400 hover:text-amber-500 transition-colors">Our Clergy</Link></li>
              <li><Link to="/archdeaconries" className="text-gray-400 hover:text-amber-500 transition-colors">Archdeaconries</Link></li>
              <li><Link to="/events" className="text-gray-400 hover:text-amber-500 transition-colors">Events</Link></li>
              <li><Link to="/support" className="text-gray-400 hover:text-amber-500 transition-colors">Support & Donations</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-lg font-semibold mb-6 border-b border-gray-800 pb-2">Resources</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/gallery" className="text-gray-400 hover:text-amber-500 transition-colors">Photo Gallery</Link></li>
              <li><Link to="/videos" className="text-gray-400 hover:text-amber-500 transition-colors">Video Gallery</Link></li>
              <li><Link to="/news" className="text-gray-400 hover:text-amber-500 transition-colors">News & Updates</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 border-b border-gray-800 pb-2">Contact Us</h4>
            <div className="space-y-3 text-gray-400 text-sm">
              <p>Diocese of Ibadan South</p>
              <p>Cathedral Church of St. David, Kudeti</p>
              <p>Ibadan, Oyo State, Nigeria</p>
              <p className="text-amber-500 pt-2">info@ibadansouthanglicandiocese.org.ng</p>
            </div>

            <div className="flex flex-wrap gap-4 mt-6">
              {socials.facebook && (
                <a href={socials.facebook.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-amber-500 hover:text-slate-900 transition-all">
                  <SiFacebook className="h-4 w-4" />
                </a>
              )}
              {socials.youtube && (
                <a href={socials.youtube.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-amber-500 hover:text-slate-900 transition-all">
                  <SiYoutube className="h-4 w-4" />
                </a>
              )}
              {socials.tiktok && (
                <a href={socials.tiktok.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-amber-500 hover:text-slate-900 transition-all">
                  <SiTiktok className="h-4 w-4" />
                </a>
              )}
              {socials.instagram && (
                <a href={socials.instagram.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-amber-500 hover:text-slate-900 transition-all">
                  <SiInstagram className="h-4 w-4" />
                </a>
              )}
              {socials.twitter && (
                <a href={socials.twitter.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-amber-500 hover:text-slate-900 transition-all">
                  <Twitter className="h-4 w-4" />
                </a>
              )}
              {socials.whatsapp?.url && (
                <a href={socials.whatsapp.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-800 rounded-full hover:bg-amber-500 hover:text-slate-900 transition-all">
                  <SiWhatsapp className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-xs">
          <p>&copy; {new Date().getFullYear()} Diocese of Ibadan South, Anglican Communion. All rights reserved.</p>
          <p className="mt-2">Website powered by <span className="text-gray-400 font-semibold">IBSD Media Team</span>.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;